import {
    ActionFunctionArgs,
    Form,
    redirect,
    useLoaderData,
    useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import {Input} from "../../../components/inputs/Input";
import PriceInput from "../../../components/inputs/PriceInput";
import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {CREATE_ORDER_AHEAD_ITEM} from "./mutations";
import {useEffect, useState} from "react";
import {Categories} from "../../../types/item";
import {userStore} from "../../../store/user";
import CustomSelect from "../../../components/customSelect";
import {organizationStore} from "../../../store/organization";
import {useEventStore} from "../../../store/event";
import {GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION} from "../../../queries";
import {Event} from "../../../types/event";
import {GET_ITEM_CATEGORIES} from "./queries";
import TicketingItemHTML from "../components/TickingItemHTML";
import {Vendor} from "../../users/type";
import {round} from "lodash";
import {ModifierGroup} from "../../../types/orderAhead";
import {PrimaryButton, SecondaryButton, TertiaryButton} from "../../../components/Button";
import {Breadcrumbs} from "../../../components/Breadcrumbs";
import {OrderAheadFormSection} from "../components/OrderAheadFormSection";
import CompactThumbUploader from "../../../components/CompactThumbUploader";

const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationStore} = organizationStore;

async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });
}

function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
}


export const orderAheadItemCreateLoader = async () => {
    const organizationId = getOrganizationStore().organizationId;
    const user = getState().user;

    let eventWhere = {};
    let vendorWhere = {};

    if (user?.vendors) {
        vendorWhere = {
            ...vendorWhere,
            id: {
                _in: user?.vendors,
            },
        };
    }

    if (user?.events) {
        eventWhere = {
            ...eventWhere,
            id: {
                _in: user?.events,
            },
        };
    }

    if (organizationId) {
        vendorWhere = {
            ...vendorWhere,
            organization_id: {
                _eq: organizationId,
            },
        };
        eventWhere = {
            ...eventWhere,
            organization_id: {_eq: organizationId},
            is_active: {_eq: true},
        };
    }
    const {data: vendorsData} = await client.query({
        query: GET_LIST_VENDORS_BY_ORGANIZATION,
        variables: {
            where: vendorWhere,
        },
    });

    const {data: eventsData} = await client.query({
        query: GET_LIST_EVENTS_BY_ORGANIZATION,
        variables: {
            where: eventWhere,
        },
    });

    const {data: itemCategoriesData} = await client.query({
        query: GET_ITEM_CATEGORIES,
        variables: {
            where: organizationId
                ? {
                    organization_id: {
                        _in: [0, organizationId],
                    },
                    is_active: {
                        _eq: true,
                    },
                    reference_type: {
                        _eq: "item_category",
                    },
                }
                : {},
        },
    });
    return {
        vendors: vendorsData.vendors,
        events: eventsData.events,
        itemCategories: itemCategoriesData.dashboard_reference,
    };
};
export const orderAheadItemCreateAction = async ({
                                                    request,
                                                }: ActionFunctionArgs) => {
    const body = await request.formData();
    const user = getState().user;
    const applyInclusivePrice = body.get("tax_type") === "exclusive" ? true : false
    const inclusivePriceString = body.get("inclusive_price") as string;
    const inclusivePrice = parseFloat(inclusivePriceString.length ? inclusivePriceString : "0");
    const priceString = body.get("price") as string;
    const price = parseFloat(priceString.length ? priceString : "0");
    const taxPercentageString = body.get("tax_percentage") as string;
    const taxPercentage = (parseFloat(taxPercentageString) / 100).toFixed(8);
    const modifiers = JSON.parse(body.get("modifiers_json") as string);



    let input = {
        name: body.get("name"),
        is_active: body.get("is_active"),
        available_public_sale: body.get("available_public_sale"),
        event_id: body.get("event_id"),
        vendor_id: body.get("vendor_id"),
        category: body.get("category"),
        description: body.get("description"),
        additional_email_description: body.get("additional_email_description"),
        price: Math.round((applyInclusivePrice ? price : inclusivePrice) * 100),
        tax_type: body.get("tax_type"),
        tax_percentage: taxPercentage ? taxPercentage : 0,
        modifiers: modifiers || [],
        image: body.get("image") || null,
        last_updated_by: user?.id,
    };

    try {
        await client.mutate({
            mutation: CREATE_ORDER_AHEAD_ITEM,
            variables: {
                input,
            },
        });

        toast.success("Order Ahead Item created");

        return redirect(`/order-ahead/overview`);
    } catch (error: any) {
        const errorType = error?.graphQLErrors?.[0]?.extensions?.code;
        const message = error?.graphQLErrors?.[0]?.message;
        if (errorType === "constraint-violation") {
            toast.error(message);
        } else {
            toast.error("Failed to create order ahead item" + error);
        }
        return redirect("/order-ahead-items/create");
    }
};

export const OrderAheadItemCreate = () => {
    const navigate = useNavigate();
    const headerEventId = useEventStore((s) => s.eventId);
    const [price, setPrice] = useState<number>(0);
    const [taxPercentage, setTaxPercentage] = useState(0);
    const [content, setContent] = useState<string>("");
    const [additionalContent, setAdditionalContent] = useState<string>("");
    const [taxType, setTaxType] = useState("exclusive");
    const [inclusivePrice, setInclusivePrice] = useState<number>(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [totalTax, setTotalTax] = useState(0);
    const {vendors, events, itemCategories} = useLoaderData() as {
        vendors: Vendor[];
        events: Event[];
        itemCategories: Categories[];
    };
    const [modifiers, setModifiers] = useState<ModifierGroup[]>([]);
    const [thumbBase64, setThumbBase64] = useState<string>("");

    const handleThumbReady = async (blob: Blob) => {
        const dataUrl = await blobToDataURL(blob);
        setThumbBase64(dataUrlToRawBase64(dataUrl));
    };
    const handleClearImage = () => setThumbBase64("");

    const handlePriceChange = (value: number) => {
        setPrice(value);
    };
    const handleTaxTypeChange = (value: string) => {
        setTaxType(value);
    };
    const handleTaxPercentageChange = (value: string | number | undefined) => {
        if (typeof value === "string") {
            setTaxPercentage(parseFloat(value) || 0);
        }
    };

    const calculateTotals = () => {
        let totalLineItemTax = 0;
        const mainTaxPercentage = taxPercentage || 0;

        if (price === 0) {
            setInclusivePrice(0);
            setTotalTax(0);
            setGrandTotal(0);
            return;
        }

        if (taxType === "inclusive") {
            const mainTaxRate = mainTaxPercentage / 100;
            const lineItemTaxRate = totalLineItemTax / price;
            const combinedTaxRate = mainTaxRate + lineItemTaxRate;
            setInclusivePrice( round((price / (1 + combinedTaxRate)),2));
            const calculatedTax = round((price - inclusivePrice),2);
            setTotalTax(calculatedTax);
            setGrandTotal(price);
        } else {
            const mainTax = (price * mainTaxPercentage) / 100;
            const totalTax = mainTax + totalLineItemTax;
            setTotalTax(totalTax);
            setGrandTotal(price + totalTax);
        }
    };

    useEffect(() => {
        calculateTotals();
    }, [taxType, price, taxPercentage, calculateTotals]);
    return (
        <Form
            method="post"
            action={`/order-ahead-items/create`}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
        >
            <div className="min-h-screen bg-gray-100 p-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <Breadcrumbs />
                    </div>
                </div>

                <OrderAheadFormSection title="Item Details">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-5 sm:col-span-3">
                            <Input label="Order Ahead Item Name" name="name" required/>
                        </div>
                        <IndeterminateCheckbox
                            label="Is Active?"
                            name="is_active"
                            defaultChecked
                        />
                        <div className="col-span-2">
                            <IndeterminateCheckbox
                                label="Available for Public Sale? -uncheck for Comp Items"
                                name="available_public_sale"
                                defaultChecked={true}
                            />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Event"
                                name="event_id"
                                required
                                defaultValue={headerEventId ?? undefined}
                                options={[
                                    ...(events?.map((e) => ({
                                        label: e.name,
                                        value: e.id,
                                    })) || []),
                                ]}
                            />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Vendor"
                                name="vendor_id"
                                required
                                options={[
                                    {
                                        value: 0,
                                        label: "All Vendors",
                                    },
                                    ...(vendors?.map((v) => ({
                                        label: v.name,
                                        value: v.id,
                                    })) || []),
                                ]}
                            />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Item Category"
                                name="category"
                                required
                                options={[
                                    ...(itemCategories?.map((v) => ({
                                        label: v.reference_label,
                                        value: v.reference_value,
                                    })) || []),
                                ]}
                            />
                        </div>
                        <div className="col-span-6">
                            <TicketingItemHTML
                                content={content}
                                setContent={setContent}
                                label="Description"
                            />
                            <Input type="hidden" name="description" value={content} />
                        </div>
                        <div className="col-span-6">
                            <TicketingItemHTML
                                content={additionalContent}
                                setContent={setAdditionalContent}
                                label="Additional Email Confirmation Description"
                            />
                            <Input type="hidden" name="additional_email_description" value={additionalContent} />
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Item Image">
                    <div className="flex items-center gap-4">
                        {thumbBase64 ? (
                            <div className="relative w-[125px] h-[125px]">
                                <img
                                    src={`data:image/webp;base64,${thumbBase64}`}
                                    width={125}
                                    height={125}
                                    alt="Item"
                                    className="rounded border border-slate-200 object-cover w-[125px] h-[125px]"
                                />
                                <button
                                    type="button"
                                    onClick={handleClearImage}
                                    aria-label="Remove image"
                                    title="Remove image"
                                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90"
                                >
                                    ×
                                </button>
                            </div>
                        ) : (
                            <div className="w-[125px] h-[125px] rounded border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                                No image
                            </div>
                        )}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center">
                                Upload Image
                                <CompactThumbUploader onReady={handleThumbReady} />
                            </h3>
                            <p className="text-xs text-gray-400 mt-1">
                                Shown to customers on the order-ahead menu.
                            </p>
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Pricing">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-5 sm:col-span-2">
                            <PriceInput
                                label="Price"
                                name="price"
                                onChange={(value) => handlePriceChange(value ? value : 0)}
                                value={price}
                                required
                            />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                            <CustomSelect
                                title="Tax Type"
                                name="tax_type"
                                options={[
                                    {label: "Inclusive", value: "inclusive"},
                                    {label: "Exclusive", value: "exclusive"},
                                ]}
                                defaultValue={"exclusive"}
                                onChange={handleTaxTypeChange}
                            />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                            <Input
                                label="Tax % ( 7.5 = 7.5% )"
                                name="tax_percentage"
                                required
                                defaultValue={0}
                                onChange={handleTaxPercentageChange}
                            />
                        </div>
                        <div className="col-span-6 text-gray-700">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Price Preview</h3>
                            <div>Price: ${taxType === "inclusive" ? inclusivePrice.toFixed(2) : price.toFixed(2)}</div>
                            <div>Total Tax: ${totalTax.toFixed(2)}</div>
                            <div>Grand Total: ${grandTotal.toFixed(2)}</div>
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Modifiers">
                    {modifiers.map((mod, groupIdx) => (
                        <div key={groupIdx} className="border rounded-lg p-4 mb-4 bg-gray-50 grid grid-cols-6 gap-4">
                            <div className="col-span-5">
                                <Input
                                    label="Modifier Group Name"
                                    name="modifier_group_name"
                                    value={mod.name}
                                    onChange={(val) => {
                                        const updated = [...modifiers];
                                        updated[groupIdx].name = val as string;
                                        setModifiers(updated);
                                    }}
                                />
                            </div>
                            <div className="col-span-1 flex items-end">
                                <PrimaryButton
                                    type="button"
                                    onClick={() => {
                                        setModifiers((prev) => prev.filter((_, i) => i !== groupIdx));
                                    }}
                                >
                                    Remove Group
                                </PrimaryButton>
                            </div>
                            <div className="col-span-2">
                                <CustomSelect
                                    title="Selection Type"
                                    value={mod.type}
                                    onChange={(val) => {
                                        const updated = [...modifiers];
                                        updated[groupIdx].type = val;
                                        setModifiers(updated);
                                    }}
                                    options={[
                                        { label: "Single", value: "single" },
                                        { label: "Multi", value: "multi" },
                                    ]}
                                />
                            </div>
                            <div className="col-span-2 mt-0.5">
                                <IndeterminateCheckbox
                                    label="Required"
                                    name={`mod-required-${groupIdx}`}
                                    defaultChecked={mod.required}
                                    onChange={(val) => {
                                        const updated = [...modifiers];
                                        updated[groupIdx].required = val as boolean;
                                        setModifiers(updated);
                                    }}
                                />
                            </div>
                            <div className="col-span-6">
                                <h4 className="font-semibold text-md mb-2 text-gray-700">Options</h4>
                                {mod.options.map((opt, optIdx) => (
                                    <div key={optIdx} className="grid grid-cols-6 gap-4 mb-4 items-end">
                                        <div className="col-span-2">
                                            <Input
                                                label="Option Name"
                                                name="option_name"
                                                value={opt.label}
                                                onChange={(val) => {
                                                    const updated = [...modifiers];
                                                    updated[groupIdx].options[optIdx].label = val as string;
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <PriceInput
                                                label="Additional Price"
                                                name="additional_price"
                                                value={opt.price / 100}
                                                onChange={(val) => {
                                                    const updated = [...modifiers];
                                                    updated[groupIdx].options[optIdx].price = round((val || 0) * 100);
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-1 mb-4">
                                            <IndeterminateCheckbox
                                                label="Default Selected"
                                                name="default_selected"
                                                defaultChecked={opt.default_selected}
                                                onChange={(val) => {
                                                    const updated = [...modifiers];
                                                    updated[groupIdx].options[optIdx].default_selected = val as boolean;
                                                    setModifiers(updated);
                                                }}
                                            />
                                        </div>

                                        <div className="col-span-1 flex items-end">
                                            <TertiaryButton
                                                type="button"
                                                onClick={() => {
                                                    const updated = [...modifiers];
                                                    updated[groupIdx].options.splice(optIdx, 1);
                                                    setModifiers(updated);
                                                }}
                                            >
                                                Remove Option
                                            </TertiaryButton>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-2">
                                    <TertiaryButton
                                        type="button"
                                        onClick={() => {
                                            const updated = [...modifiers];
                                            updated[groupIdx].options.push({ label: "", price: 0, default_selected: false });
                                            setModifiers(updated);
                                        }}
                                    >
                                        Add Option
                                    </TertiaryButton>
                                </div>
                            </div>
                        </div>
                    ))}
                    <SecondaryButton
                        type="button"
                        onClick={() => {
                            setModifiers((prev) => [
                                ...prev,
                                {
                                    name: "",
                                    type: "single",
                                    required: false,
                                    options: [{ label: "", price: 0, default_selected: false }],
                                },
                            ]);
                        }}
                    >
                        Add Modifier Group
                    </SecondaryButton>
                </OrderAheadFormSection>

                <input
                    type="hidden"
                    value={inclusivePrice.toFixed(2)}
                    name="inclusive_price"
                />
                <input type="hidden" name="modifiers_json" value={JSON.stringify(modifiers)} />
                <input type="hidden" name="image" value={thumbBase64} />

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold rounded-lg shadow-sm bg-red-700 text-white hover:bg-red-800 transition-colors"
                    >
                        Save
                    </button>
                </div>
            </div>
        </Form>
    );
};
